import { FormControl, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { VitalsInputProps } from "../../../../../../components/widgets/modals/encounter";
import CodingSystems from "../../../../../../constants/CodingSystems";
import useMedicalEncounter from "../../../../../../hooks/useMedicalEncounter";
import usePractitioner from "../../../../../../hooks/usePractitioner";
import { testRegExp } from "../../../../../../utils/regex";
import {
  calculateWaistFeedback,
  waistCheckDisplay,
} from "../../../../../../utils/waistUtils";
import { createDefaultVital } from ".";
import { Feedback } from "../types/commonCDSTypes";
import { getSimpleVitalValue, updateMetaData } from "../VitalsFormHelper";
import CdsArrowTooltip from "./cdsArrowTooltip";
import CdsFeedbackTextField from "./CdsFeedbackTextField";
import VitalCdsUnitTypography from "./VitalCdsUnitTypography";

export default function WaistInput({
  value,
  disabled,
  size,
  encounter,
  patient,
  onChange,
}: VitalsInputProps) {
  const practitioner = usePractitioner();
  const { currentPatient } = useMedicalEncounter();
  const [feedback, setFeedback] = useState<Feedback>();
  const [showClassification, setShowClassification] = useState(false);

  const waistValue = getSimpleVitalValue(value);
  const gender = currentPatient?.gender;
  const classification =
    waistValue != null
      ? waistCheckDisplay(waistValue, gender)
      : { classification: "", displayColour: "black" };

  useEffect(() => {
    if (waistValue == null) {
      setFeedback(undefined);
      setShowClassification(false);
      return;
    }

    setFeedback(calculateWaistFeedback(waistValue, gender));
    setShowClassification(true);
    const timeout = setTimeout(() => {
      setShowClassification(false);
    }, 2500);
    return () => clearTimeout(timeout);
  }, [waistValue, gender]);

  const handleChange = (event) => {
    const targetValue = event.target.value;
    const valid = testRegExp(
      "non-negative decimal number or empty string",
      targetValue,
    );
    if (!valid) return;

    const vital = createDefaultVital(
      encounter,
      patient,
      CodingSystems.OBSERVATION_CODE,
      "56115-9",
      "Waist Circumference",
      "cm",
      targetValue === "" ? null : targetValue,
    );
    updateMetaData(vital, practitioner);
    onChange(vital);
  };

  return (
    <Grid item xs={12} position="relative">
      <FormControl fullWidth>
        {waistValue != null && classification.classification && (
          <Typography
            variant="caption"
            sx={{
              color: classification.displayColour,
              fontWeight: "bold",
              opacity: showClassification ? 1 : 0,
              transition: "opacity 0.5s ease-in-out",
              height: showClassification ? "auto" : 0,
              overflow: "hidden",
              position: "absolute",
              bottom: "100%",
              left: "50%",
              transform: "translate(-50%, 0%)",
              zIndex: 1,
            }}
          >
            {classification.classification}
          </Typography>
        )}
        <CdsArrowTooltip
          type={feedback?.type}
          feedbackText={feedback?.message}
          openCDS={waistValue != null}
        >
          <CdsFeedbackTextField
            feedbackType={waistValue != null ? feedback?.type : undefined}
            id="vitals-waist"
            value={value?.valueQuantity?.value ?? ""}
            disabled={disabled}
            label="Waist"
            inputMode="decimal"
            InputProps={{
              endAdornment: (
                <VitalCdsUnitTypography
                  disabled={disabled}
                  feedbackType={waistValue != null ? feedback?.type : undefined}
                >
                  cm
                </VitalCdsUnitTypography>
              ),
            }}
            size={size}
            variant="outlined"
            type="text"
            onChange={handleChange}
          />
        </CdsArrowTooltip>
      </FormControl>
    </Grid>
  );
}
